// 生图请求：上传照片 → 读事件流（心跳 / 结果 / 错误）→ 取回结果图。所有报错都转成看得懂的中文。

import type { StyleRequest } from "../../shared/styles";
import type { Photo } from "./db";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code = "unknown",
  ) {
    super(message);
  }
}

export class AuthError extends ApiError {
  constructor() {
    super("口令失效了，请重新输入", "auth");
  }
}

const NETWORK_MSG = "网络中断了，检查一下网络再点一次";

function isNetworkError(e: unknown) {
  return e instanceof TypeError || (e as { name?: string })?.name === "NetworkError";
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface Generated {
  blob: Blob;
  cache: "HIT" | "MISS";
}

export async function generateImage(
  photo: Photo,
  req: StyleRequest,
  accessCode: string,
  onBeat?: (seconds: number) => void,
): Promise<Generated> {
  // 先把 IndexedDB 里的照片读进内存再上传：iOS Safari 直接上传 IDB 里的 Blob 偶尔会 "Load failed"
  const bytes = await photo.base.arrayBuffer();
  const headers: Record<string, string> = accessCode ? { "x-access-code": accessCode } : {};
  const makeForm = () => {
    const form = new FormData();
    form.set("image", new Blob([bytes], { type: "image/jpeg" }), "photo.jpg");
    form.set("width", String(photo.width));
    form.set("height", String(photo.height));
    form.set("styleId", req.styleId);
    form.set("colorId", req.colorId);
    if (req.custom) form.set("custom", req.custom);
    return form;
  };

  // 上传阶段断网就自动重试一次（服务端还没开始画，不会重复扣额度）
  let res: Response | null = null;
  for (let attempt = 0; attempt < 2 && !res; attempt++) {
    try {
      res = await fetch("/api/generate", { method: "POST", body: makeForm(), headers });
    } catch (e) {
      if (!isNetworkError(e) || attempt === 1) throw new ApiError(NETWORK_MSG, "network");
      await sleep(1500);
    }
  }
  if (!res) throw new ApiError(NETWORK_MSG, "network");
  return consume(res, headers, onBeat);
}

/** 生成一张发型示例图（文生图，不用家人照片） */
export async function generateSampleImage(styleId: string, accessCode: string): Promise<Generated> {
  const headers: Record<string, string> = accessCode ? { "x-access-code": accessCode } : {};
  let res: Response;
  try {
    res = await fetch(`/api/sample/${styleId}/generate`, { method: "POST", headers });
  } catch {
    throw new ApiError(NETWORK_MSG, "network");
  }
  return consume(res, headers);
}

/** 读生成接口的事件流，最后取回结果图 */
async function consume(
  res: Response,
  headers: Record<string, string>,
  onBeat?: (seconds: number) => void,
): Promise<Generated> {
  if (res.status === 401) throw new AuthError();
  if (!res.ok || !res.body) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new ApiError(body.error || `生成失败（${res.status}），稍后再试`, body.code);
  }

  // 读事件流
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  type Done = { url?: string; data?: string; mime?: string; cache?: "HIT" | "MISS" };
  let done = null as Done | null;
  try {
    while (!done) {
      const { value, done: end } = await reader.read();
      if (end) break;
      buf += dec.decode(value, { stream: true });
      let nl = buf.indexOf("\n");
      while (nl >= 0) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        nl = buf.indexOf("\n");
        if (!line) continue;
        const ev = JSON.parse(line) as { t: string; s?: number; error?: string; code?: string };
        if (ev.t === "wait") onBeat?.(ev.s ?? 0);
        else if (ev.t === "error") throw new ApiError(ev.error ?? "生成失败，稍后再试", ev.code);
        else if (ev.t === "done") done = ev as unknown as Done;
      }
    }
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError("等结果时网络断了。如果模型已经画完，再点一次会直接出来", "network");
  } finally {
    reader.cancel().catch(() => {});
  }
  if (!done) throw new ApiError("等结果时网络断了。如果模型已经画完，再点一次会直接出来", "network");

  if (done.data) {
    const bin = atob(done.data);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return { blob: new Blob([arr], { type: done.mime ?? "image/jpeg" }), cache: done.cache ?? "MISS" };
  }

  // 结果在 R2 里，取回来（失败重试一次）
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(done.url ?? "", { headers });
      if (r.status === 401) throw new AuthError();
      if (r.ok) return { blob: await r.blob(), cache: done.cache ?? "MISS" };
      if (attempt === 1) throw new ApiError(`取结果失败（${r.status}），再点一次试试`, "download");
    } catch (e) {
      if (e instanceof ApiError) throw e;
      if (attempt === 1) throw new ApiError(NETWORK_MSG, "network");
    }
    await sleep(1000);
  }
  throw new ApiError(NETWORK_MSG, "network");
}

/** 任何异常转成一句中文 */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (isNetworkError(e)) return NETWORK_MSG;
  const msg = e instanceof Error ? e.message : String(e);
  return `出了点问题：${msg}`;
}
